import React, { useCallback, useEffect, useState } from 'react';

import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import LinearProgress from '@material-ui/core/LinearProgress';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import { FieldArray, Formik } from 'formik';
import _ from 'lodash';
import { getStandardButtonLabel } from 'utils';

import { AssignmentDialogModes } from 'pages/Teachers/shared/AssignmentDialog';
import { FIRST_PAGE } from 'pages/Teachers/shared/AssignmentDialog/constants';
import {
  assignmentDetail,
  trackerCreate,
  trackerUpdate
} from 'pages/Teachers/shared/AssignmentDialog/sdk';
import {
  buildInitialValues,
  dialogValidationSchema
} from 'pages/Teachers/shared/AssignmentDialog/utils';
import AttachmentDropzone from 'pages/Teachers/shared/AttachmentDropzone';
import AttachmentList from 'pages/Teachers/shared/AttachmentList';
import AttachmentUploadOverlay from 'pages/Teachers/shared/AttachmentUploadOverlay';
import MaterialsButton from 'pages/Teachers/shared/MaterialsButton';
import TasksForm from 'pages/Teachers/shared/TasksForm';
import {
  buildFormDataForCreate,
  buildFormDataForUpdate
} from 'utils/assignments';
import { setFormErrors } from 'utils/forms';
import { useDialog } from 'utils/hooks';
import { notifyErrors } from 'utils/notifications';

import Button from 'components/Button';
import DebouncedField from 'components/DebouncedField';
import EmojiInputField from 'components/EmojiInputField';
import InputField from 'components/InputField';
import StandardButton from 'components/StandardButton';
import StandardDialog from 'components/StandardDialog';

import styles from './styles.module.scss';

const AssignmentForm = ({
  stepper,
  trackerId,
  mode,
  isAssigned,
  onCreateSuccess,
  onUpdateSuccess,
  defaultAssignmentName = ''
}) => {
  const [initialValues, setInitialValues] = useState();

  const {
    isOpened: isStandardDialogOpened,
    closeDialog: closeStandardDialog,
    openDialog: openStandardDialog
  } = useDialog();

  const initialize = useCallback(async () => {
    let newInitialValues = {
      assignmentName: defaultAssignmentName,
      description: '',
      tasks: [FIRST_PAGE],
      attachments: []
    };

    if (trackerId) {
      const isCopyMode = mode === AssignmentDialogModes.COPY;
      const assignment = await fetchAssignmentDetail({ trackerId });

      newInitialValues = {
        ...newInitialValues,
        ...buildInitialValues({ isCopyMode, assignment })
      };
    }

    setInitialValues(newInitialValues);
  }, [mode, trackerId, defaultAssignmentName]);

  useEffect(() => {
    if (!initialValues) {
      initialize();
    }
  }, [initialValues, initialize]);

  const createTracker = useCallback(
    async ({ formData, onError }) => {
      const { success, errors, data } = await trackerCreate(formData);

      if (success) {
        await onCreateSuccess(data);
      } else {
        onError(errors);
      }

      return success;
    },
    [onCreateSuccess]
  );

  const updateTracker = useCallback(
    async ({ formData, onError }) => {
      const { success, errors } = await trackerUpdate({
        trackerId,
        data: formData
      });

      if (success) {
        await onUpdateSuccess();
      } else {
        onError(errors);
      }

      return success;
    },
    [trackerId, onUpdateSuccess]
  );

  const fetchAssignmentDetail = async ({ trackerId }) => {
    const { data, success, errors } = await assignmentDetail(trackerId);

    if (success) {
      return data;
    } else {
      notifyErrors(errors);
    }
  };

  const onSubmit = async ({ values, setFieldError }) => {
    if (mode === AssignmentDialogModes.UPDATE) {
      const formData = buildFormDataForUpdate({ values });
      const onError = (errors) => setFormErrors(errors, setFieldError);
      return await updateTracker({ formData, onError });
    } else {
      const formData = buildFormDataForCreate({ values });
      const onError = (errors) => setFormErrors(errors, setFieldError);
      return await createTracker({ formData, onError });
    }
  };

  const onStandardsSelected = ({
    selectedStandards,
    formikTasks,
    setFieldValue
  }) => {
    formikTasks.forEach((task, taskIndex) => {
      const objectiveStandardsFieldName = `tasks.${taskIndex}.standards`;

      setFieldValue(objectiveStandardsFieldName, selectedStandards);
    });
  };

  const TasksFormComponent = (props) => (
    <TasksForm {...props} isAssigned={isAssigned} />
  );

  return initialValues ? (
    <Formik
      enableReinitialize
      validationSchema={dialogValidationSchema}
      initialValues={initialValues}
      onSubmit={(values, actions) =>
        onSubmit({ values, setFieldError: actions.setFieldError })
      }
    >
      {({ errors, isSubmitting, handleSubmit, values, setFieldValue }) => {
        const assignmentStandards = _.chain(values.tasks)
          .map('standards')
          .flatten()
          .uniqBy('id')
          .value();

        return (
          <AttachmentDropzone
            mode={mode}
            setFieldValue={setFieldValue}
            initialAttachments={values.attachments}
          >
            {({ attachments, onRemove, isDragActive, openFileDialog }) => (
              <>
                {isDragActive && <AttachmentUploadOverlay />}
                <DialogTitle disableTypography className={styles.dialogHeader}>
                  <div className={styles.stepperContainer}>{stepper}</div>

                  <Button
                    color="pink"
                    disabled={isSubmitting || !_.isEmpty(errors)}
                    onClick={handleSubmit}
                    disableElevation
                    endIcon={<ChevronRightRoundedIcon />}
                  >
                    {mode === AssignmentDialogModes.UPDATE
                      ? 'Update'
                      : 'Create'}
                  </Button>
                </DialogTitle>
                <DialogContent>
                  <div className={styles.container}>
                    <DebouncedField
                      value={values.assignmentName}
                      component={EmojiInputField}
                      hideEmojiTogglerOnInactiveInput
                      autoFocus
                      fullWidth
                      onChange={(value) =>
                        setFieldValue('assignmentName', value)
                      }
                      error={!_.isNil(errors.assignmentName)}
                      helperText={errors.assignmentName}
                      type="text"
                      placeholder="Assignment Name"
                      variant="underlined"
                      classes={{ root: styles.name }}
                      EmojiButtonProps={{ tabIndex: '-1' }}
                    />

                    <DebouncedField
                      value={values.description}
                      component={InputField}
                      multiline
                      fullWidth
                      minRows={1}
                      onChange={(value) => setFieldValue('description', value)}
                      error={!_.isNil(errors.description)}
                      helperText={errors.description}
                      type="text"
                      placeholder="Optional Description..."
                      variant="underlined"
                      classes={{ root: styles.description }}
                    />

                    <StandardButton
                      disabled={values.tasks.length === 0}
                      label={getStandardButtonLabel({
                        standards: assignmentStandards
                      })}
                      blue={assignmentStandards.length > 0}
                      onClick={openStandardDialog}
                      className={styles.standardButton}
                    />

                    <MaterialsButton
                      className={styles.materialsButton}
                      onClick={openFileDialog}
                    />

                    <AttachmentList
                      attachments={attachments}
                      onRemove={onRemove}
                    />

                    <FieldArray name="tasks" component={TasksFormComponent} />

                    {isSubmitting && (
                      <LinearProgress className={styles.linearProgress} />
                    )}
                  </div>
                </DialogContent>
                {isStandardDialogOpened && (
                  <StandardDialog
                    selectedItems={assignmentStandards}
                    onClose={closeStandardDialog}
                    onSave={({ selectedStandards }) =>
                      onStandardsSelected({
                        selectedStandards,
                        formikTasks: values.tasks,
                        setFieldValue
                      })
                    }
                  />
                )}
              </>
            )}
          </AttachmentDropzone>
        );
      }}
    </Formik>
  ) : null;
};

export default AssignmentForm;
